/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { setTitle } from "@lemmonhq/orders";
import { useQuery } from "@tanstack/vue-query";
import {
  getDateRange,
  syncedRef,
  useLocalization,
  type DateRange,
} from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";
import { sharedDateRangeKey } from "@app/vue/composables/sharedDateRangeKey";
import { settingsAPI } from "@app/data/api/settings";
import {
  getShiftTimes,
} from "@app/vue/pages/Reports/subpages/Overview/utils/shiftWindow";
import {
  type CatalogEntry,
  type DataRoomTab,
  defaultParameters,
  newTabId,
  normalizedKey,
  paramsToQuery,
  queryToParams,
  type ReportTimeWindow,
} from "./state";
import { DataRoomTabs } from "./components/DataRoomTabs";
import { DataRoomToolbar } from "./components/DataRoomToolbar";
import { ReportCatalog } from "./components/ReportCatalog";
import { ReportSummary } from "./components/ReportSummary";
import { ReportChart } from "./components/ReportChart";
import { ReportTable } from "./components/ReportTable";
import { CategoryReportTable } from "./components/CategoryReportTable";
import { useDataRoomExports } from "./exports";

/** Reports whose detail table renders as a category → product tree instead of a flat table. */
const CATEGORY_TREE_DEFINITIONS = new Set(["sales.categories", "sales.tax"]);

const ROWS_PAGE_LIMIT = 50;

export const DataRoomPage = defineComponent({
  name: "DataRoomPage",
  setup() {
    const { t } = useLocalization();
    const route = useRoute();
    const router = useRouter();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";

    // Export state lives at the page level so downloads survive tab switches and
    // multiple reports can be exported concurrently.
    const exportsMgr = useDataRoomExports(trpc);

    onMounted(() => setTitle(t("reports.dataroom.title")));

    // --- Tabs live in memory; the URL is the source of truth for the active tab. ---
    const tabs = ref<DataRoomTab[]>([]);
    const activeTabId = ref<string | null>(null);
    const activeTab = computed(
      () => tabs.value.find((tb) => tb.id === activeTabId.value) ?? null,
    );

    // --- Catalog + filter options ---
    const catalogQuery = useQuery({
      queryKey: ["dataroom", "catalog"],
      queryFn: () => trpc.dataroom.catalog.query() as Promise<CatalogEntry[]>,
    });
    const filterOptionsQuery = useQuery({
      queryKey: ["dataroom", "filterOptions"],
      queryFn: () => trpc.dataroom.filterOptions.query(),
    });

    // Data Room reports are time-aware, so a new tab defaults to the
    // restaurant's own opening hours rather than midnight-to-midnight.
    const settingsQuery = useQuery({
      queryKey: ["restaurant", "settings"],
      queryFn: () => settingsAPI.getSettings(),
      staleTime: 5 * 60 * 1000,
    });

    /** Settings store `HH:mm` or `HH:mm:ss`; the picker only handles `HH:mm`. */
    const toHHmm = (value: string, fallback: string) => {
      const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? "").trim());
      if (!match) return fallback;
      return `${match[1].padStart(2, "0")}:${match[2]}`;
    };

    const timeWindow = computed<ReportTimeWindow>(() => {
      const shift = getShiftTimes(settingsQuery.data.value?.data);
      return {
        startTime: toHHmm(shift.startTime, "00:00"),
        endTime: toHHmm(shift.endTime, "23:59"),
      };
    });

    // The range is shared with the other report pages. This one is time-aware,
    // so a range arriving from a date-only page gets the restaurant's opening
    // hours layered on — as a getter, because settings load asynchronously.
    const syncedDateRange = syncedRef(
      sharedDateRangeKey(
        auth.user.value?.restaurant_id,
        auth.user.value?.id,
      ),
      { type: "this_month", ...getDateRange("this_month") },
      () => timeWindow.value,
    );
    const catalog = computed<CatalogEntry[]>(
      () => catalogQuery.data.value ?? [],
    );
    const activeEntry = computed<CatalogEntry | null>(() => {
      const id = activeTab.value?.definitionId;
      if (!id) return null;
      return catalog.value.find((c) => c.definitionId === id) ?? null;
    });

    // --- URL <-> active tab reconciliation ---
    let suppressRouteWatch = false;

    const writeUrl = (tab: DataRoomTab | null) => {
      suppressRouteWatch = true;
      const query =
        tab && tab.definitionId
          ? paramsToQuery(tab.definitionId, tab.parameters)
          : {};
      router.replace({ query }).finally(() => {
        suppressRouteWatch = false;
      });
    };

    // Adopt the URL: reuse a matching tab or create one. Called on mount + navigation.
    const adoptUrl = () => {
      const { definitionId, parameters } = queryToParams(
        route.query,
        timeWindow.value,
        syncedDateRange.value,
      );
      if (!definitionId) {
        // No report in URL — ensure at least one (blank) tab is active.
        if (!tabs.value.length) {
          const blank: DataRoomTab = {
            id: newTabId(),
            definitionId: null,
            parameters: defaultParameters(timeWindow.value, syncedDateRange.value),
          };
          tabs.value.push(blank);
          activeTabId.value = blank.id;
        }
        return;
      }
      const key = normalizedKey(parameters);
      const match = tabs.value.find(
        (tb) =>
          tb.definitionId === definitionId &&
          normalizedKey(tb.parameters) === key,
      );
      if (match) {
        activeTabId.value = match.id;
        return;
      }
      // Prefer filling the active blank tab, else open a new tab.
      const blank =
        activeTab.value && !activeTab.value.definitionId
          ? activeTab.value
          : null;
      if (blank) {
        blank.definitionId = definitionId;
        blank.parameters = parameters;
        activeTabId.value = blank.id;
      } else {
        const tab: DataRoomTab = {
          id: newTabId(),
          definitionId,
          parameters,
        };
        tabs.value.push(tab);
        activeTabId.value = tab.id;
      }
    };

    // The first adopt waits for the settings request to settle so the initial
    // tab already carries the restaurant's opening hours. `isFetched` flips on
    // error too, so a failed settings call still initialises the page (with the
    // whole-day fallback) rather than leaving it tab-less.
    const initialAdoptDone = ref(false);
    const adoptOnce = () => {
      if (initialAdoptDone.value) return;
      initialAdoptDone.value = true;
      adoptUrl();
    };
    watch(
      () => settingsQuery.isFetched.value,
      (fetched) => {
        if (fetched) adoptOnce();
      },
      { immediate: true },
    );
    onMounted(() => {
      if (settingsQuery.isFetched.value) adoptOnce();
    });

    // Back/forward or external URL changes reconcile into the in-memory tabs.
    watch(
      () => route.query,
      () => {
        if (suppressRouteWatch || !initialAdoptDone.value) return;
        adoptUrl();
      },
    );

    // --- Tab actions ---
    const selectTab = (id: string) => {
      activeTabId.value = id;
      writeUrl(tabs.value.find((tb) => tb.id === id) ?? null);
    };
    const newTab = () => {
      const tab: DataRoomTab = {
        id: newTabId(),
        definitionId: null,
        parameters: defaultParameters(timeWindow.value, syncedDateRange.value),
      };
      tabs.value.push(tab);
      activeTabId.value = tab.id;
      writeUrl(tab);
    };
    const closeTab = (id: string) => {
      const idx = tabs.value.findIndex((tb) => tb.id === id);
      if (idx < 0) return;
      tabs.value.splice(idx, 1);
      if (activeTabId.value === id) {
        const next = tabs.value[Math.max(0, idx - 1)] ?? null;
        activeTabId.value = next?.id ?? null;
        if (!next) newTab();
        else writeUrl(next);
      }
    };
    const selectReport = (definitionId: string) => {
      const tab = activeTab.value;
      if (!tab) return;
      tab.definitionId = definitionId;
      tab.parameters = defaultParameters(timeWindow.value, syncedDateRange.value);
      writeUrl(tab);
    };

    // Filter changes: patch the active tab and mirror to the URL (debounced).
    let filterTimer: ReturnType<typeof setTimeout> | null = null;
    onBeforeUnmount(() => filterTimer && clearTimeout(filterTimer));
    const onFilterChange = (patch: Record<string, unknown>) => {
      const tab = activeTab.value;
      if (!tab) return;
      const nextParameters = { ...tab.parameters, ...patch };
      if (normalizedKey(nextParameters) === normalizedKey(tab.parameters)) {
        return;
      }
      // A completed artifact belongs to the exact parameters it was created
      // with. Clear it as soon as those parameters change so the toolbar never
      // offers a stale download for the newly displayed report.
      exportsMgr.resetTab(tab.id);
      tab.parameters = nextParameters;
      if (filterTimer) clearTimeout(filterTimer);
      filterTimer = setTimeout(() => writeUrl(tab), 250);
    };

    // Picking a range here moves the shared value the other report pages read.
    // Only the range travels: payment method, staff and the rest stay per-tab.
    const onRangeChange = (range: DateRange) => {
      syncedDateRange.value = range;
    };

    // --- Data queries (summary + keyset rows) ---
    const activeParamsKey = computed(() =>
      activeTab.value ? normalizedKey(activeTab.value.parameters) : "",
    );

    const summaryQuery = useQuery({
      queryKey: computed(() => [
        "dataroom",
        activeTab.value?.definitionId,
        "summary",
        activeParamsKey.value,
      ]),
      enabled: computed(() => !!activeTab.value?.definitionId),
      queryFn: () =>
        trpc.dataroom.summary.query({
          definitionId: activeTab.value!.definitionId!,
          parameters: activeTab.value!.parameters,
        }),
    });

    // Rows: keyset pagination accumulated in memory.
    const rows = ref<Array<Record<string, unknown>>>([]);
    const rowsLoading = ref(false);
    const rowsLoadingMore = ref(false);
    const rowsHasMore = ref(false);
    const rowsCursor = ref<unknown>(null);
    const reportRefreshing = ref(false);

    const loadFirstPage = async () => {
      const tab = activeTab.value;
      if (!tab?.definitionId) {
        rows.value = [];
        return;
      }
      rowsLoading.value = true;
      rowsCursor.value = null;
      try {
        const page = await trpc.dataroom.rows.query({
          definitionId: tab.definitionId,
          parameters: tab.parameters,
          limit: ROWS_PAGE_LIMIT,
          cursor: null,
        });
        rows.value = page.data as Array<Record<string, unknown>>;
        rowsHasMore.value = page.hasMore;
        rowsCursor.value = page.nextCursor;
      } finally {
        rowsLoading.value = false;
      }
    };

    const loadMore = async () => {
      const tab = activeTab.value;
      if (!tab?.definitionId || !rowsHasMore.value) return;
      rowsLoadingMore.value = true;
      try {
        const page = await trpc.dataroom.rows.query({
          definitionId: tab.definitionId,
          parameters: tab.parameters,
          limit: ROWS_PAGE_LIMIT,
          cursor: rowsCursor.value,
        });
        rows.value = [
          ...rows.value,
          ...(page.data as Array<Record<string, unknown>>),
        ];
        rowsHasMore.value = page.hasMore;
        rowsCursor.value = page.nextCursor;
      } finally {
        rowsLoadingMore.value = false;
      }
    };

    const refreshReport = async () => {
      if (!activeTab.value?.definitionId || reportRefreshing.value) return;
      // Refresh may load newer underlying data with otherwise identical
      // parameters, so the previous artifact is no longer current.
      exportsMgr.resetTab(activeTab.value.id);
      reportRefreshing.value = true;
      try {
        await Promise.all([summaryQuery.refetch(), loadFirstPage()]);
      } finally {
        reportRefreshing.value = false;
      }
    };

    // Reload summary/rows whenever the active report or its params change.
    watch(
      () => [activeTab.value?.definitionId, activeParamsKey.value],
      () => loadFirstPage(),
      { immediate: true },
    );

    // --- Render ---
    const renderBody = () => {
      const entry = activeEntry.value;
      if (!activeTab.value?.definitionId || !entry) {
        return (
          <ReportCatalog
            entries={catalog.value}
            loading={catalogQuery.isLoading.value}
            onSelect={selectReport}
          />
        );
      }
      const summary = summaryQuery.data.value ?? null;
      // A leading accent bar rather than a full-width rule: it reads as a
      // heading at a glance without drawing another grey line across the page.
      const sectionTitle = (key: string) => (
        <h2 class="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-accent">
          <span class="h-3.5 w-1 rounded-full bg-primary" aria-hidden="true" />
          {t(key)}
        </h2>
      );
      return (
        <div class="mx-auto flex w-full max-w-[1400px] flex-col gap-5 py-4 sm:gap-6 sm:py-5">
          {/*
            Each section is a real surface. Previously these were bare
            <section> elements with no classes, so the content floated
            directly on the page background with only a text label to separate
            it — which is what made the page read as an undifferentiated grey
            field.
          */}
          <section class="rounded-2xl border bg-base-300 p-3 shadow-sm sm:p-4">
            {sectionTitle("reports.dataroom.section_overview")}
            <ReportSummary
              spec={entry.interactive.summary}
              summary={summary ?? undefined}
              currency={currency()}
              loading={summaryQuery.isLoading.value}
            />
          </section>
          <section class="rounded-2xl border bg-base-200 p-3 shadow-sm sm:p-4">
            {sectionTitle(
              entry.interactive.chart.titleKey ??
                "reports.dataroom.section_trend",
            )}
            <ReportChart
              spec={entry.interactive.chart}
              summary={summary ?? undefined}
              currency={currency()}
              loading={summaryQuery.isLoading.value}
            />
          </section>
          <section>
            {sectionTitle("reports.dataroom.section_details")}
            {/* The table brings its own card, so wrapping it in another one
                would double the border. */}
            {CATEGORY_TREE_DEFINITIONS.has(entry.definitionId) ? (
              <CategoryReportTable
                spec={entry.interactive.table}
                rows={rows.value}
                currency={currency()}
                loading={rowsLoading.value}
                loadingMore={rowsLoadingMore.value}
                hasMore={rowsHasMore.value}
                onLoadMore={loadMore}
              />
            ) : (
              <ReportTable
                spec={entry.interactive.table}
                rows={rows.value}
                currency={currency()}
                loading={rowsLoading.value}
                loadingMore={rowsLoadingMore.value}
                hasMore={rowsHasMore.value}
                onLoadMore={loadMore}
                parameters={activeTab.value.parameters}
                onParameterChange={onFilterChange}
              />
            )}
          </section>
        </div>
      );
    };

    return () => (
      <div class="h-full flex flex-col flex-1 overflow-hidden bg-base-200/40">
        {/* Header */}
        {/*
          Compact header: the icon and subtitle are desktop-only. On a phone
          every vertical pixel above the data costs a row of the report, and
          the page title is already in the nav.
        */}
        <div class="bg-base-100 pt-3 sm:pt-4">
          <div class="mb-2.5 flex items-center gap-2.5 sm:mb-3 px-3 sm:px-6">
            <span class="hidden h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary sm:grid">
              <span class="material-symbols-outlined text-xl">database</span>
            </span>
            <div class="min-w-0">
              <h1 class="text-base font-semibold leading-tight text-base-content sm:text-lg">
                {t("reports.dataroom.title")}
              </h1>
              <p class="hidden text-xs leading-tight text-base-content/65 sm:block">
                {t("reports.dataroom.subtitle")}
              </p>
            </div>
          </div>
          <DataRoomTabs
            tabs={tabs.value}
            activeTabId={activeTabId.value ?? undefined}
            catalog={catalog.value}
            onSelect={selectTab}
            onClose={closeTab}
            onNew={newTab}
          />
        </div>

        <DataRoomToolbar
          entry={activeEntry.value ?? undefined}
          tab={activeTab.value ?? undefined}
          options={filterOptionsQuery.data.value}
          timeWindow={timeWindow.value}
          range={syncedDateRange.value}
          refreshing={
            reportRefreshing.value ||
            summaryQuery.isFetching.value ||
            rowsLoading.value
          }
          run={
            activeTab.value
              ? (exportsMgr.runForTab(activeTab.value.id) ?? undefined)
              : undefined
          }
          onFilterChange={onFilterChange}
          onRangeChange={onRangeChange}
          onRefresh={refreshReport}
          onStart={(kind) =>
            exportsMgr.startExport(
              activeTab.value!.id,
              activeTab.value!.definitionId!,
              activeTab.value!.parameters,
              kind,
            )
          }
          onReset={() => exportsMgr.resetTab(activeTab.value!.id)}
          onDownload={(id) => exportsMgr.download(id)}
        />

        <div class="min-h-0 flex-1 overflow-y-auto px-3 sm:px-6">
          {renderBody()}
        </div>
      </div>
    );
  },
});
